import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import OperatorCard from "@/components/OperatorCard";
import SEO from "@/components/SEO";
import { Search } from "lucide-react";

const TABS = [
  { key: "all", label: "All operators", hint: "16 operators" },
  { key: "ota", label: "OTAs", hint: "Booking, Hotels.com, Expedia…" },
  { key: "meta", label: "Meta-search", hint: "Kayak, Trivago, Tripadvisor" },
  { key: "chain", label: "Chain loyalty", hint: "Marriott, Hilton, Hyatt…" },
];

export default function OperatorsPage() {
  const [operators, setOperators] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/operators")
      .then(({ data }) => setOperators(data))
      .finally(() => setLoaded(true));
  }, []);

  const filtered = useMemo(() => {
    const base = active === "all" ? operators : operators.filter((o) => o.kind === active);
    const withSearch = !query
      ? base
      : base.filter((o) => {
          const q = query.toLowerCase();
          return (
            o.name.toLowerCase().includes(q) ||
            o.tagline.toLowerCase().includes(q) ||
            (o.loyalty?.name || "").toLowerCase().includes(q)
          );
        });
    // Pin Choice Hotels to the top when present in the filtered result
    const featured = withSearch.find((o) => o.key === "choice");
    if (!featured) return withSearch;
    return [featured, ...withSearch.filter((o) => o.key !== "choice")];
  }, [operators, active, query]);

  const heroOps = filtered.slice(0, 3);
  const gridOps = filtered.slice(3);

  return (
    <div>
      <SEO
        title="Hotel operators compared"
        description="A side-by-side guide to every major US hotel operator — Booking.com, Hotels.com, Expedia, Marriott Bonvoy, Hilton Honors, Hyatt, IHG, Choice, Wyndham and more."
      />

      {/* HERO HEADER */}
      <section className="bg-charcoal text-sand relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-30" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl bg-terracotta/30 pointer-events-none" />
        <div className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full blur-3xl bg-forest/40 pointer-events-none" />

        <div className="relative container-x py-16 md:py-24 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline text-terracotta-light">The operators</div>
            <h1 className="mt-4 font-serif text-5xl md:text-7xl tracking-tight leading-[0.95] text-balance">
              Every US hotel booking source, scored and compared.
            </h1>
            <p className="mt-6 text-sand/80 text-lg max-w-2xl leading-relaxed">
              16 operators. Five criteria. One honest editorial score. Start with the source you trust most — or the one your loyalty points already live in.
            </p>
          </div>
          <div className="md:col-span-4">
            <div className="grid grid-cols-3 gap-4">
              {[
                { k: "Operators", v: "16" },
                { k: "OTAs", v: "5" },
                { k: "Chains", v: "8" },
              ].map((s) => (
                <div key={s.k} className="border border-sand/15 p-4">
                  <div className="overline text-sand/50">{s.k}</div>
                  <div className="font-serif text-4xl mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs + search */}
        <div className="relative container-x pb-10 md:pb-12 flex flex-wrap items-center gap-3" data-testid="operator-kind-tabs">
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={`tab-kind-${t.key}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                  active === t.key
                    ? "bg-sand text-charcoal border-sand"
                    : "bg-transparent text-sand/75 border-sand/20 hover:border-sand/50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[220px] ml-auto max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sand/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search operators"
              data-testid="operator-search"
              className="w-full bg-sand/10 border border-sand/15 text-sand placeholder:text-sand/40 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-sand/50"
            />
          </div>
        </div>
      </section>

      {/* HERO MOSAIC + GRID */}
      <section className="container-x py-14 md:py-16">
        {!loaded ? (
          <div data-testid="operators-loading">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-10">
              {[0, 1, 2].map((i) => (
                <div key={i} className="aspect-[4/5] md:aspect-[5/6] bg-charcoal/5 animate-pulse" />
              ))}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-64 bg-charcoal/5 animate-pulse" />
              ))}
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-charcoal/10 bg-white p-14 text-center" data-testid="operators-empty">
            <div className="overline mb-2">No match</div>
            <div className="font-serif text-2xl">Try a different filter or search.</div>
          </div>
        ) : (
          <>
            {heroOps.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-10" data-testid="operators-hero">
                {heroOps.map((op) => (
                  <OperatorCard key={op.key} operator={op} variant="hero" featured={op.key === "choice"} />
                ))}
              </div>
            )}
            {gridOps.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="operators-grid">
                {gridOps.map((op) => (
                  <OperatorCard key={op.key} operator={op} featured={op.key === "choice"} />
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
